import { Component, Host, h, Element, Prop, Method } from '@stencil/core';
import { Type, Position } from './types';
import './aha-toast-window-events';

export interface AhaToast extends HTMLAhaToastElement {}

/**
 * @slot default - Contents of the default slot.
 * @slot icon - Contents of the icon slot, otherwise defaults to a type-specific icon.
 */
@Component({
  tag: 'aha-toast',
  styleUrl: 'aha-toast.css',
  shadow: true,
})
export class AhaToast {
  @Element() host!: HTMLAhaToastElement;

  @Prop({ reflect: true }) type: Type = 'success';

  @Prop({ reflect: true }) duration: number = 3000;

  // Whether or not the toast needs to be dismissed manually
  @Prop({ reflect: true }) persistent: boolean = false;

  // Whether or not the toast is currently being dismissed.
  @Prop({ reflect: true }) dismissed: boolean = false;

  // The position of the toast on the screen.
  @Prop({ reflect: true }) position: Position = 'bottom-right';

  connectedCallback() {
    this.host.parentElement?.querySelectorAll(':scope aha-toast')?.forEach(toast => {
      if (toast !== this.host) {
        // @ts-ignore
        toast.dismiss();
      }
    });
  }

  componentDidLoad() {
    const timerElem = this.host.shadowRoot?.querySelector('#timer');
    if (timerElem) {
      timerElem.addEventListener('animationend', (e: any) => {
        if (e.animationName === 'countdown') {
          this.dismiss();
        }
      });
    }
  }

  disconnectedCallback() {
    this.dismissed = false;
  }

  @Method()
  async dismiss() {
    this.dismissed = true;
    this.host.addEventListener('transitionend', e => {
      if (e.propertyName === 'opacity') {
        this.host.remove();
      }
    });
  }

  @Method()
  async show({
    type,
    duration,
    persistent,
    position,
  }: {
    type?: Type;
    duration?: number;
    persistent?: boolean;
    position?: Position;
  } = {}) {
    this.dismissed = false;
    this.type = type || this.type;
    this.duration = duration || this.duration;
    this.persistent = persistent || this.persistent;
    this.position = position || this.position;
    document.body.appendChild(this.host);
  }

  getIcon() {
    switch (this.type) {
      case 'success':
        return 'fa-solid fa-circle-check';
      case 'danger':
        return 'fa-solid fa-triangle-exclamation';
      case 'warning':
        return 'fa-solid fa-circle-exclamation';
      case 'info':
        return 'fa-solid fa-circle-info';
    }
  }

  render() {
    return (
      <Host onClick={() => this.dismiss()}>
        <div id="wrapper">
          <div>
            <slot name="icon">
              <aha-icon icon={this.getIcon()}></aha-icon>
            </slot>
          </div>
          <slot></slot>
          {this.persistent && (
            <div id="close">
              <button class="close">
                <aha-icon icon="fa-solid fa-close"></aha-icon>
              </button>
            </div>
          )}
        </div>
        <div id="timer" style={{ '--aha-toast--duration': `${this.duration}ms` }}></div>
      </Host>
    );
  }
}
